/*
 * Head office, franchises and the security center as React Query hooks (draft 11). A change refreshes every head office
 * answer, since one role or plan moves the dashboard, the people list and the security center together.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ActivityRow,
  HqPersonRow,
  LocationLine,
  HubSignIn,
  OverviewAnswer,
  PlaceAnswer,
  PlanAnswer,
  ReviewState,
  Role,
  SecurityAnswer,
  TerritoryRow,
  PlanPut,
  VaultGraphAnswer,
} from "@steward/shared";
import { api } from "./client";
import { qs } from "./hooks";

const KEYS = [
  "hq-overview",
  "hq-territories",
  "hq-people",
  "hq-franchises",
  "franchise",
  "security",
  "security-activity",
  "security-hub",
];

function useRefresh() {
  const qc = useQueryClient();
  return () => Promise.all(KEYS.map((k) => qc.invalidateQueries({ queryKey: [k] })));
}

export const useOverview = (companyId: string) =>
  useQuery({ queryKey: ["hq-overview", companyId], queryFn: () => api<OverviewAnswer>("GET", "hq/overview") });

/** The owner's notes as a graph (one login only), checked again every 5 minutes. Anyone else gets not found, so it never retries and the card stays hidden. */
export const useVaultGraph = (enabled: boolean) =>
  useQuery({
    queryKey: ["hq-vault-graph"],
    queryFn: () => api<VaultGraphAnswer>("GET", "hq/vault-graph"),
    enabled,
    retry: false,
    refetchInterval: 5 * 60_000, // a note edited since fires a burst when the new copy lands
  });

export const useTerritories = (companyId: string, enabled = true) =>
  useQuery({
    queryKey: ["hq-territories", companyId],
    queryFn: () => api<{ rows: TerritoryRow[] }>("GET", "hq/territories"),
    enabled,
  });

export function useSetTerritory() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (v: { companyId: string; zips: string[] }) =>
      api<{ zips: string[] }>("PUT", `hq/territories/${v.companyId}`, { zips: v.zips }),
    onSuccess: refresh,
  });
}

/** Head office sets a location's sub locations, the whole list at once. */
export function useSetSubLocations() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (v: { companyId: string; lines: LocationLine[] }) =>
      api<{ lines: LocationLine[] }>("PUT", `hq/sub-locations/${v.companyId}`, { lines: v.lines }),
    onSuccess: refresh,
  });
}

export const usePlace = () =>
  useMutation({ mutationFn: (v: { zip?: string; address?: string }) => api<PlaceAnswer>("POST", "hq/place", v) });

export const useHqPeople = (companyId: string) =>
  useQuery({ queryKey: ["hq-people", companyId], queryFn: () => api<HqPersonRow[]>("GET", "hq/people") });

export function useGiveRole() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (v: { userId: string; companyId: string; role: Role }) =>
      api<{ ok: true }>("PUT", `hq/people/${v.userId}/roles/${v.companyId}`, { role: v.role }),
    onSuccess: refresh,
  });
}

export function useEndRole() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (v: { userId: string; companyId: string }) =>
      api<{ ok: true }>("DELETE", `hq/people/${v.userId}/roles/${v.companyId}`),
    onSuccess: refresh,
  });
}

export function useLoginOff() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (v: { userId: string; off: boolean }) =>
      api<{ ok: true }>("POST", `hq/people/${v.userId}/${v.off ? "off" : "on"}`),
    onSuccess: refresh,
  });
}

export const useFranchises = (companyId: string) =>
  useQuery({ queryKey: ["hq-franchises", companyId], queryFn: () => api<PlanAnswer[]>("GET", "hq/franchises") });

export function useSavePlan() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (v: { companyId: string; body: PlanPut }) => api<PlanAnswer>("PUT", `hq/franchises/${v.companyId}`, v.body),
    onSuccess: refresh,
  });
}

export function useChargeNow() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (companyId: string) => api<PlanAnswer>("POST", `hq/franchises/${companyId}/charge`),
    onSuccess: refresh,
  });
}

export const usePlan = (companyId: string, enabled: boolean) =>
  useQuery({ queryKey: ["franchise", companyId], queryFn: () => api<PlanAnswer>("GET", "franchise"), enabled });

export function useCardSetup() {
  return useMutation({
    mutationFn: () =>
      api<{ setupId: string; clientSecret: string; publishableKey: string; fake: boolean }>("POST", "franchise/card"),
  });
}

export function useCardConfirm() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (setupId: string) => api<PlanAnswer>("POST", "franchise/card/confirm", { setupId }),
    onSuccess: refresh,
  });
}

export const useSecurity = (companyId: string) =>
  useQuery({ queryKey: ["security", companyId], queryFn: () => api<SecurityAnswer>("GET", "security") });

export function useMarkReviewed() {
  const refresh = useRefresh();
  return useMutation({ mutationFn: () => api<ReviewState>("POST", "security/review"), onSuccess: refresh });
}

export function useSignOutLogin() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (userId: string) => api<{ devices: number }>("POST", `security/logins/${userId}/sign-out`),
    onSuccess: refresh,
  });
}

export function useSignOutDevice() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (sessionId: string) => api<{ ok: true }>("POST", `security/devices/${sessionId}/sign-out`),
    onSuccess: refresh,
  });
}

export function useSignOutEveryone() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: () => api<{ logins: number; devices: number }>("POST", "security/sign-out-everyone"),
    onSuccess: refresh,
  });
}

export const useHubSignIns = (companyId: string) =>
  useQuery({ queryKey: ["security-hub", companyId], queryFn: () => api<HubSignIn[]>("GET", "security/hub") });

export function useHubSignOut() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (v: { sessionId: string; all: boolean }) => api<{ devices: number }>("POST", "security/hub/sign-out", v),
    onSuccess: refresh,
  });
}

export function useRemoveAccess() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (userId: string) => api<{ companies: number }>("POST", `security/logins/${userId}/remove`),
    onSuccess: refresh,
  });
}

export const useActivity = (companyId: string, filter: string) =>
  useQuery({
    queryKey: ["security-activity", companyId, filter],
    queryFn: () => api<ActivityRow[]>("GET", `security/activity${qs(filter ? { companyId: filter } : {})}`),
  });
